(() => {
  'use strict';

  const root = document.querySelector('[data-vaniz-intent-test-root]');
  if (!(root instanceof HTMLElement)) return;

  const contactForm = document.getElementById('VanizIntentTestContactForm');
  const priorityForm = document.getElementById('VanizIntentTestPriorityForm');
  const dialog = root.querySelector('[role="dialog"]');
  const body = root.querySelector('[data-vaniz-intent-test-body]');
  if (!(contactForm instanceof HTMLFormElement) || !(dialog instanceof HTMLElement) || !(body instanceof HTMLElement)) return;

  const progressScreens = ['details', 'review', 'payment'];
  const editableScreens = [...progressScreens];
  const validScreens = new Set(progressScreens);
  const entrySources = new Set(['cart', 'buy_now']);
  const trafficChannels = new Set(['paid_social', 'organic_search', 'direct', 'referral', 'unknown']);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const pendingContextKey = 'vaniz-intent-test-pending-context-v3';
  const completedContextKey = 'vaniz-intent-test-completed-context-v1';
  const attributionContextKey = 'vaniz-intent-test-attribution-context-v2';
  const eventLedgerKey = 'vaniz-intent-test-event-ledger-v1';
  const contextMaximumAge = 30 * 60 * 1000;
  const internationalShippingAmount = 2990;
  const klaviyoCompanyId = 'RpMkyS';
  const klaviyoRevision = '2026-07-15';
  const klaviyoMetricName = 'VANIZ Continue to Payment';
  const klaviyoRequestTimeout = 6000;
  const utmSourceValues = new Set(['meta', 'facebook', 'instagram', 'tiktok', 'google', 'bing', 'organic', 'direct']);
  const utmMediumValues = new Set(['paid_social', 'paid-social', 'social', 'cpc', 'organic', 'retargeting', 'direct']);
  const campaignTokenPattern = /^(vaniz|qa)[._-][a-z0-9][a-z0-9._-]*$/i;

  let currentScreen = 'details';
  let historyDepth = 0;
  let ignoreNextPopState = false;
  let scrollPosition = 0;
  let inertedElements = [];
  let entryFocusContext = null;
  let commercialContext = null;
  let funnelEventId = '';
  let attribution = captureAttribution();
  const screenMotionTimers = new WeakMap();

  for (const key of [
    'vaniz-intent-test-context-v1',
    'vaniz-intent-test-pending-context-v2',
    'vaniz-intent-test-attribution-context-v1',
  ]) {
    clearSessionKey(key);
  }

  function sanitizeText(value, maximumLength = 200) {
    if (typeof value !== 'string') return '';
    return value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, maximumLength);
  }

  function message(key, fallback) {
    return sanitizeText(root.dataset[key], 300) || fallback;
  }

  function toNonNegativeInteger(value) {
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= 0 ? number : null;
  }

  function toPositiveInteger(value) {
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : null;
  }

  function readSessionJson(key) {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_error) {
      return null;
    }
  }

  function writeSessionJson(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function clearSessionKey(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      return;
    }
  }

  function isFreshTimestamp(value) {
    if (!Number.isSafeInteger(value)) return false;
    const age = Date.now() - value;
    return age >= 0 && age <= contextMaximumAge;
  }

  function sanitizeUtmValue(key, value) {
    const maximumLength = key === 'utm_campaign' ? 80 : 64;
    const token = sanitizeText(value, maximumLength);
    if (!token || !/^[a-z0-9][a-z0-9._-]*$/i.test(token)) return '';
    if (/(email|e-mail|phone|telephone|address|name)/i.test(token)) return '';
    if (key === 'utm_source' && !utmSourceValues.has(token.toLowerCase())) return '';
    if (key === 'utm_medium' && !utmMediumValues.has(token.toLowerCase())) return '';
    if (['utm_campaign', 'utm_content', 'utm_term'].includes(key) && !campaignTokenPattern.test(token)) return '';
    return token;
  }

  function isValidAttribution(value) {
    if (!value || typeof value !== 'object' || !trafficChannels.has(value.traffic_channel)) return false;
    return utmKeys.every((key) => sanitizeUtmValue(key, value[key] || '') === (value[key] || ''));
  }

  function captureAttribution() {
    const stored = readSessionJson(attributionContextKey);
    if (stored && isFreshTimestamp(stored.created_at) && isValidAttribution(stored.attribution)) {
      return stored.attribution;
    }
    clearSessionKey(attributionContextKey);

    const parameters = new URLSearchParams(window.location.search);
    const utms = {};
    for (const key of utmKeys) utms[key] = sanitizeUtmValue(key, parameters.get(key) || '');

    const source = utms.utm_source.toLowerCase();
    const medium = utms.utm_medium.toLowerCase();
    let trafficChannel = 'unknown';
    if (['meta', 'facebook', 'instagram', 'tiktok'].includes(source) || ['paid_social', 'paid-social', 'retargeting'].includes(medium)) {
      trafficChannel = 'paid_social';
    } else if (['google', 'bing'].includes(source) && medium === 'cpc') {
      trafficChannel = 'paid_social';
    } else if (['organic', 'google', 'bing'].includes(source) && medium === 'organic') {
      trafficChannel = 'organic_search';
    } else if (source === 'direct' || medium === 'direct') {
      trafficChannel = 'direct';
    } else if (!utmKeys.some((key) => utms[key])) {
      let referrerHost = '';
      try {
        referrerHost = new URL(document.referrer).hostname.toLowerCase();
      } catch (_error) {
        referrerHost = '';
      }
      const currentHost = window.location.hostname.toLowerCase();
      if (!referrerHost || referrerHost === currentHost) trafficChannel = 'direct';
      else if (/(^|\.)(google|bing|yahoo|duckduckgo)\./.test(referrerHost)) trafficChannel = 'organic_search';
      else trafficChannel = 'referral';
    }

    const value = {
      traffic_channel: trafficChannels.has(trafficChannel) ? trafficChannel : 'unknown',
      ...utms,
    };
    writeSessionJson(attributionContextKey, { created_at: Date.now(), attribution: value });
    return value;
  }

  function createEventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
    return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  }

  function sanitizeImageUrl(value) {
    const raw = sanitizeText(value, 1000);
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      return url.href.slice(0, 1000);
    } catch (_error) {
      return '';
    }
  }

  function isValidCommercialContext(value) {
    if (!value || typeof value !== 'object' || !entrySources.has(value.entry_source)) return false;
    if (!Array.isArray(value.line_items) || !value.line_items.length) return false;
    if (!/^[A-Z]{3}$/.test(value.currency || '')) return false;
    const amounts = ['item_count', 'subtotal', 'total_discount', 'shipping_amount', 'displayed_total'];
    if (!amounts.every((key) => Number.isSafeInteger(value[key]) && value[key] >= 0)) return false;
    if (![0, internationalShippingAmount].includes(value.shipping_amount)) return false;
    if (value.total_discount > value.subtotal) return false;
    if (value.displayed_total !== value.subtotal - value.total_discount + value.shipping_amount) return false;
    const quantityTotal = value.line_items.reduce((total, line) => total + line.quantity, 0);
    if (quantityTotal !== value.item_count) return false;
    return value.line_items.every((line) =>
      toPositiveInteger(line.product_id) === line.product_id &&
      toPositiveInteger(line.variant_id) === line.variant_id &&
      toPositiveInteger(line.quantity) === line.quantity &&
      toNonNegativeInteger(line.unit_price) === line.unit_price &&
      toNonNegativeInteger(line.final_line_price) === line.final_line_price &&
      sanitizeText(line.product_title, 255) === line.product_title &&
      sanitizeText(line.variant_title, 255) === line.variant_title &&
      sanitizeImageUrl(line.image_url || '') === (line.image_url || '')
    );
  }

  function sanitizeCurrency(value) {
    const currency = sanitizeText(value, 3).toUpperCase();
    return /^[A-Z]{3}$/.test(currency) ? currency : 'USD';
  }

  function getAssociatedProductContext(button) {
    const expectedFormId = sanitizeText(button.dataset.vanizIntentTestFormId, 200);
    const form = button.closest('form');
    const actualFormId = form instanceof HTMLFormElement ? form.getAttribute('id') : '';
    if (!(form instanceof HTMLFormElement) || !expectedFormId || actualFormId !== expectedFormId) return null;

    const variantField = form.elements.namedItem('id');
    const quantityField = form.elements.namedItem('quantity');
    if (!(variantField instanceof HTMLInputElement)) return null;

    const variantId = toPositiveInteger(variantField.value);
    const quantity = quantityField instanceof HTMLInputElement ? toPositiveInteger(quantityField.value) : 1;
    if (!variantId || !quantity || variantField.disabled) return null;

    const container = button.closest('[data-vaniz-intent-test-entry-container]');
    const variantData = container?.querySelector('[data-vaniz-intent-test-variants]');
    if (!(variantData instanceof HTMLScriptElement)) return null;

    let variants;
    try {
      variants = JSON.parse(variantData.textContent || '[]');
    } catch (_error) {
      return null;
    }
    if (!Array.isArray(variants)) return null;

    const variant = variants.find((item) => toPositiveInteger(item.id) === variantId);
    const productId = toPositiveInteger(button.dataset.vanizIntentTestProductId);
    const unitPrice = toNonNegativeInteger(variant?.price);
    if (!variant || !productId || !variant.available || unitPrice === null) return null;

    const subtotal = unitPrice * quantity;
    if (!Number.isSafeInteger(subtotal)) return null;
    return {
      entry_source: 'buy_now',
      currency: sanitizeCurrency(button.dataset.vanizIntentTestCurrency),
      item_count: quantity,
      subtotal,
      total_discount: 0,
      shipping_amount: currentShippingAmount(),
      displayed_total: subtotal + currentShippingAmount(),
      line_items: [
        {
          product_id: productId,
          product_title: sanitizeText(button.dataset.vanizIntentTestProductTitle, 255),
          variant_id: variantId,
          variant_title: sanitizeText(variant.title, 255),
          quantity,
          unit_price: unitPrice,
          final_line_price: subtotal,
          image_url: sanitizeImageUrl(variant.image_url || ''),
        },
      ],
    };
  }

  function refreshBuyNowButtons(scope = document) {
    for (const button of scope.querySelectorAll('[data-vaniz-intent-test-entry="buy_now"]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const valid = Boolean(getAssociatedProductContext(button));
      if (button.disabled === valid) button.disabled = !valid;
      button.setAttribute('aria-disabled', valid ? 'false' : 'true');
      if (valid) setEntryError(button, '');
    }
  }

  function cartIsBusy() {
    return Boolean(document.querySelector('cart-items-component.cart-items-disabled, cart-items-component[aria-busy="true"]'));
  }

  function wait(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  function waitForFrames(count = 1) {
    return new Promise((resolve) => {
      const next = () => {
        if (count <= 0) resolve();
        else {
          count -= 1;
          window.requestAnimationFrame(next);
        }
      };
      next();
    });
  }

  async function waitForCartReadiness() {
    const deadline = window.performance.now() + 8000;
    await waitForFrames(2);
    if (!cartIsBusy()) await wait(75);
    while (cartIsBusy()) {
      if (window.performance.now() >= deadline) throw new Error('cart_timeout');
      await wait(75);
    }
    await waitForFrames(2);
    if (cartIsBusy()) throw new Error('cart_timeout');
  }

  async function readCartContext() {
    await waitForCartReadiness();
    if (!window.Shopify?.routes?.root) throw new Error('cart_route');
    const response = await fetch(window.Shopify.routes.root + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('cart_network');

    let cart;
    try {
      cart = await response.json();
    } catch (_error) {
      throw new Error('cart_malformed');
    }
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) throw new Error('cart_empty');

    const lineItems = cart.items.map((item) => {
      const productId = toPositiveInteger(item.product_id);
      const variantId = toPositiveInteger(item.variant_id);
      const quantity = toPositiveInteger(item.quantity);
      const unitPrice = toNonNegativeInteger(item.original_price ?? item.price);
      const finalLinePrice = toNonNegativeInteger(item.final_line_price);
      if (!productId || !variantId || !quantity || unitPrice === null || finalLinePrice === null) throw new Error('cart_malformed');
      return {
        product_id: productId,
        product_title: sanitizeText(item.product_title, 255),
        variant_id: variantId,
        variant_title: sanitizeText(item.variant_title, 255),
        quantity,
        unit_price: unitPrice,
        final_line_price: finalLinePrice,
        image_url: sanitizeImageUrl(item.image || ''),
      };
    });

    const subtotal = toNonNegativeInteger(cart.original_total_price ?? cart.items_subtotal_price);
    const totalDiscount = toNonNegativeInteger(cart.total_discount);
    const cartTotal = toNonNegativeInteger(cart.total_price);
    const itemCount = toPositiveInteger(cart.item_count);
    const quantityTotal = lineItems.reduce((total, line) => total + line.quantity, 0);
    if (subtotal === null || totalDiscount === null || cartTotal === null || !itemCount) throw new Error('cart_malformed');
    if (quantityTotal !== itemCount || cartTotal > subtotal || subtotal - cartTotal !== totalDiscount) throw new Error('cart_malformed');

    const context = {
      entry_source: 'cart',
      currency: sanitizeCurrency(cart.currency),
      item_count: itemCount,
      subtotal,
      total_discount: totalDiscount,
      shipping_amount: currentShippingAmount(),
      displayed_total: cartTotal + currentShippingAmount(),
      line_items: lineItems,
    };
    if (!isValidCommercialContext(context)) throw new Error('cart_malformed');
    return context;
  }

  async function closeContainingCartDrawer(button) {
    const drawer = button.closest('theme-drawer');
    if (!(drawer instanceof HTMLElement)) return null;
    const close = drawer.close;
    if (typeof close === 'function') await close.call(drawer);
    return drawer;
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(amount / 100);
    } catch (_error) {
      const sign = amount < 0 ? '-' : '';
      return `${sign}$${(Math.abs(amount) / 100).toFixed(2)}`;
    }
  }

  function createProductLine(line, currency, editableQuantity = false, lineIndex = 0) {
    const article = document.createElement('article');
    article.className = 'vaniz-intent-test__product-line';

    let visual;
    if (line.image_url) {
      visual = document.createElement('img');
      visual.className = 'vaniz-intent-test__product-image';
      visual.src = line.image_url;
      visual.alt = '';
      visual.width = 80;
      visual.height = 80;
      visual.loading = 'eager';
    } else {
      visual = document.createElement('span');
      visual.className = 'vaniz-intent-test__product-image vaniz-intent-test__product-image--placeholder';
      visual.setAttribute('aria-hidden', 'true');
    }

    const copy = document.createElement('div');
    copy.className = 'vaniz-intent-test__product-copy';
    const title = document.createElement('h4');
    title.className = 'vaniz-intent-test__product-title';
    title.textContent = line.product_title;
    copy.append(title);

    if (line.variant_title && line.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.className = 'vaniz-intent-test__product-variant';
      variant.textContent = line.variant_title;
      copy.append(variant);
    }

    const meta = document.createElement('p');
    meta.className = 'vaniz-intent-test__product-meta';
    meta.textContent = editableQuantity
      ? `${message('unitPriceLabel', 'Unit price')} ${formatMoney(line.unit_price, currency)}`
      : `${message('quantityLabel', 'Quantity')} ${line.quantity} · ${message('unitPriceLabel', 'Unit price')} ${formatMoney(line.unit_price, currency)}`;
    copy.append(meta);

    if (editableQuantity) {
      const quantity = document.createElement('div');
      quantity.className = 'vaniz-intent-test__quantity';
      quantity.setAttribute('aria-label', `${message('quantityLabel', 'Quantity')}: ${line.product_title}`);

      const decrease = document.createElement('button');
      decrease.className = 'vaniz-intent-test__quantity-button';
      decrease.type = 'button';
      decrease.textContent = '−';
      decrease.disabled = line.quantity <= 1;
      decrease.setAttribute('aria-label', `${message('decreaseQuantity', 'Decrease quantity')}: ${line.product_title}`);
      decrease.dataset.vanizIntentTestQuantityChange = '-1';
      decrease.dataset.vanizIntentTestLineIndex = String(lineIndex);

      const value = document.createElement('span');
      value.className = 'vaniz-intent-test__quantity-value';
      value.textContent = String(line.quantity);
      value.setAttribute('aria-live', 'polite');

      const increase = document.createElement('button');
      increase.className = 'vaniz-intent-test__quantity-button';
      increase.type = 'button';
      increase.textContent = '+';
      increase.disabled = line.quantity >= 99;
      increase.setAttribute('aria-label', `${message('increaseQuantity', 'Increase quantity')}: ${line.product_title}`);
      increase.dataset.vanizIntentTestQuantityChange = '1';
      increase.dataset.vanizIntentTestLineIndex = String(lineIndex);

      quantity.append(decrease, value, increase);
      copy.append(quantity);
    }

    const price = document.createElement('strong');
    price.className = 'vaniz-intent-test__product-price';
    price.textContent = formatMoney(line.final_line_price, currency);
    article.append(visual, copy, price);
    return article;
  }

  function createTrustBadge(label, icon) {
    const badge = document.createElement('span');
    badge.className = 'vaniz-intent-test__trust-badge';

    const badgeIcon = document.createElement('span');
    badgeIcon.className = `vaniz-intent-test__trust-badge-icon vaniz-intent-test__trust-badge-icon--${icon}`;
    badgeIcon.setAttribute('aria-hidden', 'true');

    const badgeLabel = document.createElement('span');
    badgeLabel.textContent = label;
    badge.append(badgeIcon, badgeLabel);
    return badge;
  }

  function renderProductContexts() {
    if (!isValidCommercialContext(commercialContext)) return;
    for (const container of root.querySelectorAll('[data-vaniz-intent-test-product-context]')) {
      if (!(container instanceof HTMLElement)) continue;
      container.replaceChildren();
      const review = container.dataset.vanizIntentTestProductContext === 'review';
      commercialContext.line_items.forEach((line, index) => {
        container.append(createProductLine(line, commercialContext.currency, review, index));
      });

      const badges = document.createElement('div');
      badges.className = 'vaniz-intent-test__trust-badges';
      if (review) {
        badges.append(
          createTrustBadge(message('limitedEditionLabel', 'Limited Edition'), 'edition'),
          createTrustBadge(message('designedInParisLabel', 'Designed in Paris'), 'paris')
        );
      } else {
        const shippingLabel = commercialContext.shipping_amount === 0
          ? message('freeShippingLabel', 'Free US Shipping')
          : `${message('worldwideShippingLabel', 'Worldwide Shipping')} · ${formatMoney(commercialContext.shipping_amount, commercialContext.currency)}`;
        badges.append(
          createTrustBadge(shippingLabel, 'shipping'),
          createTrustBadge(message('secureCheckoutLabel', 'Secure Checkout'), 'lock')
        );
      }
      container.append(badges);
    }
  }

  function setText(selector, value) {
    const element = root.querySelector(selector);
    if (element instanceof HTMLElement) element.textContent = value;
  }

  function renderReview() {
    if (!isValidCommercialContext(commercialContext)) return;
    renderProductContexts();
    setText('[data-vaniz-intent-test-subtotal]', formatMoney(commercialContext.subtotal, commercialContext.currency));
    setText('[data-vaniz-intent-test-discount]', formatMoney(-commercialContext.total_discount, commercialContext.currency));
    const discountRow = root.querySelector('[data-vaniz-intent-test-discount-row]');
    if (discountRow instanceof HTMLElement) discountRow.hidden = commercialContext.total_discount === 0;
    setText(
      '[data-vaniz-intent-test-shipping-label]',
      commercialContext.shipping_amount === 0
        ? message('freeShippingLabel', 'Free US Shipping')
        : message('internationalShippingLabel', 'International Shipping')
    );
    setText(
      '[data-vaniz-intent-test-shipping-display]',
      commercialContext.shipping_amount === 0
        ? message('free', 'Free')
        : formatMoney(commercialContext.shipping_amount, commercialContext.currency)
    );
    setText('[data-vaniz-intent-test-total]', formatMoney(commercialContext.displayed_total, commercialContext.currency));
  }

  function updateReviewQuantity(lineIndex, delta) {
    if (currentScreen !== 'review' || !isValidCommercialContext(commercialContext)) return;
    if (!Number.isSafeInteger(lineIndex) || ![-1, 1].includes(delta)) return;
    const currentLine = commercialContext.line_items[lineIndex];
    if (!currentLine) return;
    const nextQuantity = Math.min(99, Math.max(1, currentLine.quantity + delta));
    if (nextQuantity === currentLine.quantity) return;

    const effectiveUnitPrice = Math.min(
      currentLine.unit_price,
      Math.max(0, Math.round(currentLine.final_line_price / currentLine.quantity))
    );
    const lineItems = commercialContext.line_items.map((line, index) => index === lineIndex
      ? {
          ...line,
          quantity: nextQuantity,
          final_line_price: effectiveUnitPrice * nextQuantity,
        }
      : { ...line });
    const itemCount = lineItems.reduce((total, line) => total + line.quantity, 0);
    const subtotal = lineItems.reduce((total, line) => total + (line.unit_price * line.quantity), 0);
    const discountedSubtotal = lineItems.reduce((total, line) => total + line.final_line_price, 0);
    const totalDiscount = subtotal - discountedSubtotal;
    const nextContext = {
      ...commercialContext,
      item_count: itemCount,
      subtotal,
      total_discount: totalDiscount,
      displayed_total: discountedSubtotal + commercialContext.shipping_amount,
      line_items: lineItems,
    };
    if (!isValidCommercialContext(nextContext)) return;
    commercialContext = nextContext;
    renderReview();
  }

  function getCountryField() {
    const field = root.querySelector('[data-vaniz-intent-test-country]');
    return field instanceof HTMLSelectElement ? field : null;
  }

  function isUnitedStatesSelected() {
    return sanitizeText(getCountryField()?.value || '', 100).toLowerCase() === 'united states';
  }

  function currentShippingAmount() {
    return isUnitedStatesSelected() ? 0 : internationalShippingAmount;
  }

  function selectedCountryProvinces() {
    const country = getCountryField();
    const selected = country?.selectedOptions[0];
    if (!(selected instanceof HTMLOptionElement)) return [];
    try {
      const parsed = JSON.parse(selected.dataset.provinces || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  function updateProvinceOptions() {
    const state = root.querySelector('[data-vaniz-intent-test-state]');
    if (!(state instanceof HTMLSelectElement)) return;
    state.replaceChildren();
    const provinces = selectedCountryProvinces();
    if (!provinces.length) {
      const option = document.createElement('option');
      option.value = 'N/A';
      option.textContent = message('notApplicable', 'Not applicable');
      state.append(option);
      return;
    }
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = message('selectState', 'Select a state/province/region');
    state.append(placeholder);
    for (const province of provinces) {
      if (!Array.isArray(province) || province.length < 2) continue;
      const label = sanitizeText(province[0], 100);
      const value = sanitizeText(province[1], 100);
      if (!label || !value) continue;
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      state.append(option);
    }
  }

  function updatePostalRules() {
    const zip = root.querySelector('[data-vaniz-intent-test-zip]');
    if (!(zip instanceof HTMLInputElement)) return;
    if (isUnitedStatesSelected()) {
      zip.inputMode = 'numeric';
      zip.maxLength = 5;
      zip.pattern = '[0-9]{5}';
    } else {
      zip.inputMode = 'text';
      zip.maxLength = 12;
      zip.removeAttribute('pattern');
    }
    zip.value = '';
  }

  function updateCommercialShipping() {
    if (!commercialContext) return;
    const shipping = currentShippingAmount();
    const displayedTotal = commercialContext.subtotal - commercialContext.total_discount + shipping;
    if (!Number.isSafeInteger(displayedTotal) || displayedTotal < 0) return;
    commercialContext = {
      ...commercialContext,
      shipping_amount: shipping,
      displayed_total: displayedTotal,
    };
    renderProductContexts();
    renderReview();
  }

  function updateCountryDependentUi() {
    updateProvinceOptions();
    updatePostalRules();
    updateCommercialShipping();
    clearFieldError('country');
    clearFieldError('state');
    clearFieldError('zip');
  }

  function getDetailsFields() {
    return {
      email: root.querySelector('[data-vaniz-intent-test-email]'),
      country: root.querySelector('[data-vaniz-intent-test-country]'),
      firstName: root.querySelector('#VanizIntentTestFirstName'),
      lastName: root.querySelector('#VanizIntentTestLastName'),
      address: root.querySelector('#VanizIntentTestAddress'),
      city: root.querySelector('#VanizIntentTestCity'),
      state: root.querySelector('[data-vaniz-intent-test-state]'),
      zip: root.querySelector('[data-vaniz-intent-test-zip]'),
    };
  }

  function clearFieldError(key) {
    const fields = getDetailsFields();
    const field = fields[key];
    const error = root.querySelector(`[data-vaniz-intent-test-error="${key}"]`);
    if (field instanceof HTMLElement) field.removeAttribute('aria-invalid');
    if (error instanceof HTMLElement) {
      error.textContent = '';
      error.hidden = true;
    }
  }

  function setFieldError(key, text) {
    const fields = getDetailsFields();
    const field = fields[key];
    const error = root.querySelector(`[data-vaniz-intent-test-error="${key}"]`);
    if (field instanceof HTMLElement) field.setAttribute('aria-invalid', 'true');
    if (error instanceof HTMLElement) {
      error.textContent = text;
      error.hidden = false;
    }
  }

  function clearAllFieldErrors() {
    for (const key of ['email', 'country', 'state', 'zip']) clearFieldError(key);
    for (const field of root.querySelectorAll('[data-vaniz-intent-test-step="details"] input, [data-vaniz-intent-test-step="details"] select')) {
      if (field instanceof HTMLElement) field.removeAttribute('aria-invalid');
    }
  }

  function markRequiredField(field, invalid) {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;
    if (invalid) field.setAttribute('aria-invalid', 'true');
    else field.removeAttribute('aria-invalid');
  }

  function requiredFieldIsInvalid(field) {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return true;
    if (field instanceof HTMLSelectElement && field.value === '---') return true;
    return !field.value.trim() || !field.validity.valid;
  }

  function updateBillingPostalRules() {
    const country = root.querySelector('[data-vaniz-intent-test-billing-country]');
    const zip = root.querySelector('#VanizIntentTestBillingZip');
    if (!(country instanceof HTMLSelectElement) || !(zip instanceof HTMLInputElement)) return;
    const unitedStates = sanitizeText(country.value, 100).toLowerCase() === 'united states';
    zip.inputMode = unitedStates ? 'numeric' : 'text';
    zip.maxLength = unitedStates ? 5 : 12;
    if (unitedStates) zip.pattern = '[0-9]{5}';
    else zip.removeAttribute('pattern');
  }

  function syncBillingRequirements() {
    const toggle = root.querySelector('#VanizIntentTestBillingDifferent');
    const billingFields = root.querySelector('#VanizIntentTestBillingFields');
    if (!(toggle instanceof HTMLInputElement) || !(billingFields instanceof HTMLElement)) return;
    const active = toggle.checked;
    toggle.setAttribute('aria-expanded', active ? 'true' : 'false');
    for (const field of billingFields.querySelectorAll('input, select')) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) continue;
      field.disabled = !active;
      field.required = active && field.hasAttribute('data-vaniz-intent-test-billing-required');
      if (!active) field.removeAttribute('aria-invalid');
    }
    updateBillingPostalRules();
  }

  function validateDetails() {
    clearAllFieldErrors();
    const fields = getDetailsFields();
    const invalid = [];
    if (!(fields.email instanceof HTMLInputElement) || !fields.email.value.trim() || !fields.email.validity.valid) {
      setFieldError('email', message('errorEmail', 'Enter a valid email address.'));
      invalid.push(fields.email);
    }
    if (!(fields.country instanceof HTMLSelectElement) || !fields.country.value || fields.country.value === '---') {
      setFieldError('country', message('errorCountry', 'Select a country/region.'));
      invalid.push(fields.country);
    }
    for (const key of ['firstName', 'lastName', 'address', 'city']) {
      const field = fields[key];
      const fieldInvalid = requiredFieldIsInvalid(field);
      markRequiredField(field, fieldInvalid);
      if (fieldInvalid) invalid.push(field);
    }
    if (!(fields.state instanceof HTMLSelectElement) || !fields.state.value) {
      setFieldError('state', message('errorState', 'Select a state.'));
      invalid.push(fields.state);
    }
    const postalValue = fields.zip instanceof HTMLInputElement ? fields.zip.value.trim() : '';
    const postalValid = isUnitedStatesSelected()
      ? /^[0-9]{5}$/.test(postalValue)
      : /^[a-z0-9][a-z0-9 -]{1,11}$/i.test(postalValue);
    if (!(fields.zip instanceof HTMLInputElement) || !postalValid) {
      setFieldError(
        'zip',
        isUnitedStatesSelected()
          ? message('errorZip', 'Enter a 5-digit ZIP code.')
          : message('errorPostal', 'Enter a valid ZIP/postal code.')
      );
      invalid.push(fields.zip);
    }
    const billingToggle = root.querySelector('#VanizIntentTestBillingDifferent');
    if (billingToggle instanceof HTMLInputElement && billingToggle.checked) {
      for (const field of root.querySelectorAll('[data-vaniz-intent-test-billing-required]')) {
        const fieldInvalid = requiredFieldIsInvalid(field);
        markRequiredField(field, fieldInvalid);
        if (fieldInvalid) invalid.push(field);
      }
    }
    const firstInvalid = invalid.find((field) => field instanceof HTMLInputElement || field instanceof HTMLSelectElement);
    if (firstInvalid instanceof HTMLInputElement || firstInvalid instanceof HTMLSelectElement) {
      firstInvalid.focus();
      firstInvalid.reportValidity();
      return false;
    }
    return true;
  }

  function showError(text, trigger) {
    const alert = root.querySelector('[data-vaniz-intent-test-global-error]');
    if (alert instanceof HTMLElement) {
      alert.textContent = text;
      alert.hidden = false;
    }
    if (trigger instanceof HTMLElement) trigger.setAttribute('aria-invalid', 'true');
  }

  function clearError() {
    const alert = root.querySelector('[data-vaniz-intent-test-global-error]');
    if (alert instanceof HTMLElement) {
      alert.textContent = '';
      alert.hidden = true;
    }
  }

  function announce(text) {
    const live = root.querySelector('[data-vaniz-intent-test-live]');
    if (!(live instanceof HTMLElement)) return;
    live.textContent = '';
    window.requestAnimationFrame(() => {
      live.textContent = text;
    });
  }

  function showScreen(screen, pushHistory = true, direction = 'forward') {
    if (!validScreens.has(screen)) return;
    currentScreen = screen;
    root.dataset.vanizIntentTestScreen = screen;
    if (screen === 'review') renderReview();

    let visibleStep = null;
    for (const element of root.querySelectorAll('[data-vaniz-intent-test-step]')) {
      if (!(element instanceof HTMLElement)) continue;
      const motionTimer = screenMotionTimers.get(element);
      if (motionTimer) window.clearTimeout(motionTimer);
      const visible = element.dataset.vanizIntentTestStep === screen;
      element.hidden = !visible;
      element.removeAttribute('data-motion');
      if (visible) visibleStep = element;
    }
    if (visibleStep instanceof HTMLElement && direction !== 'none') {
      visibleStep.dataset.motion = direction;
      const motionTimer = window.setTimeout(() => {
        visibleStep.removeAttribute('data-motion');
        screenMotionTimers.delete(visibleStep);
      }, 1500);
      screenMotionTimers.set(visibleStep, motionTimer);
    }

    const activeIndex = progressScreens.indexOf(screen);
    for (const item of root.querySelectorAll('[data-vaniz-intent-test-progress]')) {
      if (!(item instanceof HTMLElement)) continue;
      const index = progressScreens.indexOf(item.dataset.vanizIntentTestProgress || '');
      item.classList.toggle('vaniz-intent-test__progress-item--active', index === activeIndex);
      item.classList.toggle('vaniz-intent-test__progress-item--complete', index >= 0 && index < activeIndex);
      if (index === activeIndex) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    }
    clearError();
    if (pushHistory && root.dataset.vanizIntentTestOpen === 'true') {
      window.history.pushState({ vanizIntentTest: true, screen }, '', window.location.href);
      historyDepth += 1;
    }

    body.scrollTop = 0;
    if (root.dataset.vanizIntentTestOpen === 'true') {
      const heading = visibleStep?.querySelector('.vaniz-intent-test__step-title');
      window.requestAnimationFrame(() => (heading instanceof HTMLElement ? heading : dialog).focus());
      const screenMessage = screen === 'details'
        ? message('screenDetails', 'Details')
        : screen === 'review'
          ? message('screenReview', 'Review')
          : message('screenPayment', 'Payment');
      announce(screenMessage);
    }
  }

  function lockPage() {
    scrollPosition = window.scrollY;
    document.body.classList.add('vaniz-intent-test-scroll-lock');
    document.body.style.top = `-${scrollPosition}px`;
    inertedElements = [];
    for (const child of document.body.children) {
      if (child === root || !(child instanceof HTMLElement)) continue;
      inertedElements.push({ element: child, ariaHidden: child.getAttribute('aria-hidden'), inert: child.inert });
      child.inert = true;
      child.setAttribute('aria-hidden', 'true');
    }
  }

  function unlockPage() {
    document.body.classList.remove('vaniz-intent-test-scroll-lock');
    document.body.style.removeProperty('top');
    for (const item of inertedElements) {
      item.element.inert = item.inert;
      if (item.ariaHidden === null) item.element.removeAttribute('aria-hidden');
      else item.element.setAttribute('aria-hidden', item.ariaHidden);
    }
    inertedElements = [];
    window.scrollTo(0, scrollPosition);
  }

  function openOverlay(pushHistory = true) {
    if (root.dataset.vanizIntentTestOpen === 'true') return;
    root.hidden = false;
    root.dataset.vanizIntentTestOpen = 'true';
    lockPage();
    if (pushHistory) {
      window.history.pushState({ vanizIntentTest: true, screen: currentScreen }, '', window.location.href);
      historyDepth = 1;
    }
    const heading = root.querySelector(`[data-vaniz-intent-test-step="${currentScreen}"] .vaniz-intent-test__step-title`);
    window.requestAnimationFrame(() => (heading instanceof HTMLElement ? heading : dialog).focus());
    announce(
      currentScreen === 'details'
        ? message('screenDetails', 'Details')
        : currentScreen === 'review'
          ? message('screenReview', 'Shipping')
          : message('screenPayment', 'Payment')
    );
  }

  async function restoreEntryFocus() {
    const context = entryFocusContext;
    entryFocusContext = null;
    if (!context) return;
    if (context.drawer instanceof HTMLElement) {
      const open = context.drawer.open;
      if (typeof open === 'function' && !context.drawer.hasAttribute('open')) {
        open.call(context.drawer);
        await waitForFrames(2);
      }
      const fresh = context.drawer.querySelector(`[data-vaniz-intent-test-entry="${context.source}"]`);
      if (fresh instanceof HTMLElement) {
        fresh.focus();
        return;
      }
    }
    if (context.element instanceof HTMLElement && context.element.isConnected) {
      context.element.focus();
      return;
    }
    const fallback = document.querySelector(`[data-vaniz-intent-test-entry="${context.source}"]`);
    if (fallback instanceof HTMLElement) fallback.focus();
  }

  function clearDetailsValues() {
    const fields = getDetailsFields();
    for (const [key, field] of Object.entries(fields)) {
      if (key === 'country') continue;
      if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) field.value = '';
    }
    for (const field of root.querySelectorAll('[data-vaniz-intent-test-transient]')) {
      if (field instanceof HTMLInputElement) {
        if (field.type === 'checkbox') field.checked = false;
        else field.value = '';
      }
    }
    const country = getCountryField();
    if (country instanceof HTMLSelectElement) {
      country.value = 'United States';
      if (!country.value && country.options.length) country.selectedIndex = 0;
    }
    const billingCountry = root.querySelector('[data-vaniz-intent-test-billing-country]');
    if (billingCountry instanceof HTMLSelectElement && country instanceof HTMLSelectElement) {
      billingCountry.value = country.value;
    }
    syncBillingRequirements();
    updateCountryDependentUi();
    clearAllFieldErrors();
    const priorityEmail = root.querySelector('[data-vaniz-intent-test-priority-email]');
    if (priorityEmail instanceof HTMLInputElement) priorityEmail.value = '';
  }

  function clearTemporaryPersonalValues() {
    for (const field of root.querySelectorAll('[data-vaniz-intent-test-transient]')) {
      if (!(field instanceof HTMLInputElement)) continue;
      if (field.type === 'checkbox') field.checked = false;
      else field.value = '';
    }
    syncBillingRequirements();
  }

  function closeOverlay(updateHistory = true) {
    if (root.dataset.vanizIntentTestOpen !== 'true') return;
    root.dataset.vanizIntentTestOpen = 'false';
    root.hidden = true;
    unlockPage();
    clearDetailsValues();
    clearPendingContext();
    clearCompletedContext();
    commercialContext = null;
    funnelEventId = '';
    showScreen('details', false, 'none');
    if (updateHistory && historyDepth > 0) {
      ignoreNextPopState = true;
      window.history.go(-historyDepth);
    }
    historyDepth = 0;
    restoreEntryFocus();
  }

  function hideOverlayForPrioritySubmit() {
    if (root.dataset.vanizIntentTestOpen !== 'true') return;
    root.dataset.vanizIntentTestOpen = 'false';
    root.hidden = true;
    unlockPage();
    restoreEntryFocus();
  }

  function getFocusableElements() {
    return Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => element instanceof HTMLElement && !element.closest('[hidden]') && element.offsetParent !== null);
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || root.dataset.vanizIntentTestOpen !== 'true') return;
    const focusable = getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function eventLedger() {
    const stored = readSessionJson(eventLedgerKey);
    if (!stored || !Array.isArray(stored.entries)) return [];
    return stored.entries.filter((entry) => entry && typeof entry.key === 'string' && isFreshTimestamp(entry.created_at));
  }

  function hasPublishedEvent(name, eventId) {
    const key = `${eventId}:${name}`;
    return eventLedger().some((entry) => entry.key === key);
  }

  function markPublishedEvent(name, eventId) {
    const entries = eventLedger();
    entries.push({ key: `${eventId}:${name}`, created_at: Date.now() });
    writeSessionJson(eventLedgerKey, { entries: entries.slice(-80) });
  }

  function analyticsValue(values) {
    return values.length === 1 ? values[0] : values;
  }

  function analyticsPayload(context) {
    return {
      event_id: context.event_id,
      entry_source: context.entry_source,
      traffic_channel: context.attribution.traffic_channel,
      utm_source: context.attribution.utm_source,
      utm_medium: context.attribution.utm_medium,
      utm_campaign: context.attribution.utm_campaign,
      utm_content: context.attribution.utm_content,
      utm_term: context.attribution.utm_term,
      product_id: analyticsValue(context.line_items.map((line) => line.product_id)),
      variant_id: analyticsValue(context.line_items.map((line) => line.variant_id)),
      quantity: analyticsValue(context.line_items.map((line) => line.quantity)),
      currency: context.currency,
      product_price: analyticsValue(context.line_items.map((line) => line.unit_price)),
      subtotal: context.subtotal,
      shipping_amount: context.shipping_amount,
      displayed_total: context.displayed_total,
    };
  }

  function currencyAmount(amount) {
    return Number((amount / 100).toFixed(2));
  }

  function internalIntentProperties(context) {
    return {
      event_id: context.event_id,
      entry_source: context.entry_source,
      traffic_channel: context.attribution.traffic_channel,
      utm_source: context.attribution.utm_source,
      utm_medium: context.attribution.utm_medium,
      utm_campaign: context.attribution.utm_campaign,
      utm_content: context.attribution.utm_content,
      utm_term: context.attribution.utm_term,
      currency: context.currency,
      item_count: context.item_count,
      subtotal: currencyAmount(context.subtotal),
      discount: currencyAmount(context.total_discount),
      shipping: currencyAmount(context.shipping_amount),
      estimated_total: currencyAmount(context.displayed_total),
      items: context.line_items.map((line) => ({
        product_id: String(line.product_id),
        variant_id: String(line.variant_id),
        product: line.product_title,
        variant: line.variant_title,
        quantity: line.quantity,
        unit_price: currencyAmount(line.unit_price),
        line_total: currencyAmount(line.final_line_price),
      })),
    };
  }

  async function sendInternalIntentEvent(context, email) {
    if (!isValidPendingContext(context) || !email || email.length > 99) return false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), klaviyoRequestTimeout);
    const payload = {
      data: {
        type: 'event',
        attributes: {
          properties: internalIntentProperties(context),
          metric: {
            data: {
              type: 'metric',
              attributes: { name: klaviyoMetricName },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          time: new Date(context.created_at).toISOString(),
          value: currencyAmount(context.displayed_total),
          value_currency: context.currency,
          unique_id: context.event_id,
        },
      },
    };

    try {
      const response = await fetch(`https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(klaviyoCompanyId)}`, {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          Revision: klaviyoRevision,
        },
        body: JSON.stringify(payload),
        keepalive: true,
        signal: controller.signal,
      });
      return response.status === 202;
    } catch (_error) {
      return false;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function setPaymentSubmitBusy(button, busy) {
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = busy;
    button.setAttribute('aria-disabled', busy ? 'true' : 'false');
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  function publishEventOnce(name, context) {
    if (!isValidPendingContext(context) || hasPublishedEvent(name, context.event_id)) return;
    markPublishedEvent(name, context.event_id);
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;
    try {
      const result = publish(name, analyticsPayload(context));
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (_error) {
      return;
    }
  }

  function buildContext(eventId) {
    if (!isValidCommercialContext(commercialContext) || !isValidAttribution(attribution)) return null;
    return {
      event_id: eventId,
      created_at: Date.now(),
      entry_source: commercialContext.entry_source,
      attribution: { ...attribution },
      currency: commercialContext.currency,
      item_count: commercialContext.item_count,
      subtotal: commercialContext.subtotal,
      total_discount: commercialContext.total_discount,
      shipping_amount: commercialContext.shipping_amount,
      displayed_total: commercialContext.displayed_total,
      line_items: commercialContext.line_items.map((line) => ({ ...line })),
    };
  }

  function isValidPendingContext(value) {
    if (!value || typeof value !== 'object' || !/^[a-f0-9-]{16,64}$/i.test(value.event_id || '')) return false;
    if (!isFreshTimestamp(value.created_at) || !isValidAttribution(value.attribution)) return false;
    return isValidCommercialContext(value);
  }

  function clearPendingContext() {
    clearSessionKey(pendingContextKey);
  }

  function readCompletedContext() {
    const completed = readSessionJson(completedContextKey);
    if (!isValidPendingContext(completed)) {
      clearCompletedContext();
      return null;
    }
    return completed;
  }

  function writeCompletedContext(value) {
    if (!isValidPendingContext(value)) return false;
    return writeSessionJson(completedContextKey, value);
  }

  function clearCompletedContext() {
    clearSessionKey(completedContextKey);
  }

  async function advanceToPayment(submitButton) {
    if (!validateDetails() || !isValidCommercialContext(commercialContext)) {
      showError(message('errorRequired', 'Please review the highlighted details.'));
      return null;
    }

    const eventId = funnelEventId || createEventId();
    const context = buildContext(eventId);
    if (!context) {
      showError(message('errorPrepare', 'We couldn’t prepare your request. Please try again.'));
      return null;
    }
    const email = root.querySelector('[data-vaniz-intent-test-email]');
    const priorityEmail = root.querySelector('[data-vaniz-intent-test-priority-email]');
    if (!(email instanceof HTMLInputElement)) {
      showError(message('errorEmail', 'Enter a valid email address.'));
      return null;
    }
    const customerEmail = sanitizeText(email.value, 99);
    setPaymentSubmitBusy(submitButton, true);
    const internalEventAccepted = await sendInternalIntentEvent(context, customerEmail);
    if (!internalEventAccepted) {
      setPaymentSubmitBusy(submitButton, false);
      showError(message('errorPrepare', 'We couldn’t prepare your request. Please try again.'));
      return null;
    }

    writeCompletedContext(context);
    if (priorityEmail instanceof HTMLInputElement) priorityEmail.value = customerEmail;
    clearTemporaryPersonalValues();
    clearPendingContext();
    publishEventOnce('vaniz_intent_test:continue_to_payment_clicked', context);
    showScreen('payment', true, 'forward');
    publishEventOnce('vaniz_intent_test:availability_viewed', context);
    setPaymentSubmitBusy(submitButton, false);
    return true;
  }

  function handlePriorityServerState() {
    const state = root.querySelector('[data-vaniz-intent-test-priority-state]');
    if (!(state instanceof HTMLElement)) return false;
    const succeeded = state.dataset.vanizIntentTestPrioritySuccess === 'true';
    const failed = state.dataset.vanizIntentTestPriorityErrors === 'true';
    if (!succeeded && !failed) return false;

    const completed = readCompletedContext();
    if (succeeded) {
      if (completed) publishEventOnce('vaniz_intent_test:priority_list_joined', completed);
      clearPendingContext();
      clearCompletedContext();
      clearDetailsValues();
      commercialContext = null;
      funnelEventId = '';
      showScreen('details', false, 'none');
      return true;
    }
    showScreen('payment', false, 'none');
    openOverlay(true);
    const focusTarget = root.querySelector('[data-vaniz-intent-test-priority-submit]');
    window.requestAnimationFrame(() => {
      if (focusTarget instanceof HTMLElement) focusTarget.focus();
    });
    announce(message('errorServer', 'We couldn’t add you to the priority list. Please try again.'));
    return true;
  }

  function setEntryError(button, text) {
    const container = button.closest('[data-vaniz-intent-test-entry-container]');
    const error = container?.querySelector('[data-vaniz-intent-test-entry-error]');
    if (error instanceof HTMLElement) {
      error.textContent = text;
      error.hidden = !text;
    }
  }

  async function handleEntryClick(button) {
    const source = button.dataset.vanizIntentTestEntry;
    if (!entrySources.has(source)) return;
    clearPendingContext();
    clearCompletedContext();
    setEntryError(button, '');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const drawer = button.closest('theme-drawer');
    entryFocusContext = { element: button, source, drawer: drawer instanceof HTMLElement ? drawer : null };
    try {
      commercialContext = source === 'cart' ? await readCartContext() : getAssociatedProductContext(button);
      if (!isValidCommercialContext(commercialContext)) throw new Error('entry_invalid');
      funnelEventId = createEventId();
      showScreen('details', false, 'none');
      renderProductContexts();
      if (source === 'cart') {
        openOverlay(true);
        await waitForFrames(2);
        await closeContainingCartDrawer(button);
      } else {
        openOverlay(true);
      }
      const context = buildContext(funnelEventId);
      if (context) publishEventOnce('vaniz_intent_test:funnel_opened', context);
    } catch (error) {
      entryFocusContext = null;
      const text = error instanceof Error && error.message === 'cart_empty'
        ? message('errorCartEmpty', 'Your cart is empty.')
        : source === 'buy_now'
          ? message('errorBuyNow', 'Select an available variant and quantity before continuing.')
          : message('errorCart', 'We couldn’t read your current cart. Please wait for the update to finish and try again.');
      setEntryError(button, text);
    } finally {
      button.removeAttribute('aria-busy');
      if (source === 'cart') button.disabled = false;
      else refreshBuyNowButtons(button.closest('[data-vaniz-intent-test-entry-container]') || document);
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const stickyEntryButton = target?.closest('[data-vaniz-intent-test-sticky-entry]');
    if (stickyEntryButton instanceof HTMLButtonElement) {
      event.preventDefault();
      const section = stickyEntryButton.closest('.shopify-section');
      const entryButton = section?.querySelector('[data-vaniz-intent-test-entry="buy_now"]');
      if (entryButton instanceof HTMLButtonElement && !entryButton.disabled) entryButton.click();
      return;
    }
    const entryButton = target?.closest('[data-vaniz-intent-test-entry]');
    if (entryButton instanceof HTMLButtonElement) {
      event.preventDefault();
      handleEntryClick(entryButton);
      return;
    }
    if (!root.contains(target)) return;
    const quantityButton = target?.closest('[data-vaniz-intent-test-quantity-change]');
    if (quantityButton instanceof HTMLButtonElement) {
      event.preventDefault();
      updateReviewQuantity(
        Number(quantityButton.dataset.vanizIntentTestLineIndex),
        Number(quantityButton.dataset.vanizIntentTestQuantityChange)
      );
      return;
    }
    const submitButton = target?.closest('[data-vaniz-intent-test-submit]');
    if (submitButton instanceof HTMLButtonElement) {
      event.preventDefault();
      void advanceToPayment(submitButton);
      return;
    }
    if (target?.closest('[data-vaniz-intent-test-close]')) {
      event.preventDefault();
      closeOverlay(true);
      return;
    }
    const next = target?.closest('[data-vaniz-intent-test-next]');
    if (next instanceof HTMLButtonElement && next.dataset.vanizIntentTestNext === 'review') {
      if (!validateDetails()) return;
      const context = buildContext(funnelEventId);
      if (context) publishEventOnce('vaniz_intent_test:details_completed', context);
      renderReview();
      showScreen('review', true, 'forward');
      if (context) publishEventOnce('vaniz_intent_test:review_viewed', context);
      return;
    }
    const back = target?.closest('[data-vaniz-intent-test-back]');
    if (back instanceof HTMLButtonElement) {
      if (window.history.state?.vanizIntentTest) window.history.back();
      else showScreen('details', false, 'back');
    }
  });

  document.addEventListener('keydown', (event) => {
    trapFocus(event);
    if (event.key === 'Escape' && root.dataset.vanizIntentTestOpen === 'true') {
      event.preventDefault();
      closeOverlay(true);
    }
  });

  window.addEventListener('popstate', (event) => {
    if (ignoreNextPopState) {
      ignoreNextPopState = false;
      return;
    }
    if (event.state?.vanizIntentTest && root.dataset.vanizIntentTestOpen === 'true') {
      historyDepth = Math.max(1, historyDepth - 1);
      const screen = editableScreens.includes(event.state.screen) ? event.state.screen : 'details';
      showScreen(screen, false, 'back');
    } else if (root.dataset.vanizIntentTestOpen === 'true') {
      closeOverlay(false);
    }
  });

  for (const [key, field] of Object.entries(getDetailsFields())) {
    if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
      field.addEventListener(field instanceof HTMLSelectElement ? 'change' : 'input', () => clearFieldError(key));
    }
  }

  for (const field of root.querySelectorAll('[data-vaniz-intent-test-step="details"] input, [data-vaniz-intent-test-step="details"] select')) {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) continue;
    field.addEventListener(field instanceof HTMLSelectElement ? 'change' : 'input', () => {
      if (field.validity.valid) field.removeAttribute('aria-invalid');
    });
  }

  const countryField = getCountryField();
  if (countryField instanceof HTMLSelectElement) {
    if (!countryField.value || countryField.value === '---') countryField.value = 'United States';
    countryField.addEventListener('change', updateCountryDependentUi);
  }

  const billingToggle = root.querySelector('#VanizIntentTestBillingDifferent');
  const billingCountry = root.querySelector('[data-vaniz-intent-test-billing-country]');
  if (billingToggle instanceof HTMLInputElement && billingCountry instanceof HTMLSelectElement) {
    billingToggle.addEventListener('change', () => {
      if (billingToggle.checked && countryField instanceof HTMLSelectElement) billingCountry.value = countryField.value;
      syncBillingRequirements();
    });
    billingCountry.addEventListener('change', updateBillingPostalRules);
  }

  if (priorityForm instanceof HTMLFormElement) {
    priorityForm.addEventListener('submit', () => {
      const button = priorityForm.querySelector('[data-vaniz-intent-test-priority-submit]');
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
      }
      hideOverlayForPrioritySubmit();
    }, true);
  }

  const observer = new MutationObserver((records) => {
    if (records.some((record) => record.addedNodes.length > 0)) refreshBuyNowButtons();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  updateCountryDependentUi();
  syncBillingRequirements();
  refreshBuyNowButtons();
  showScreen('details', false, 'none');
  const handledPriorityState = handlePriorityServerState();
  if (!handledPriorityState) {
    clearPendingContext();
    clearCompletedContext();
  }
})();
